// The Friday What's new email exactly as it will be sent, with the only
// buttons that can send it.
import { Loader2, Mail, X } from 'lucide-react';
import type { WhatsNewDraft } from '../types';

interface WhatsNewDraftModalProps {
  draft: WhatsNewDraft;
  busy: boolean;
  onApprove: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

export default function WhatsNewDraftModal({ draft, busy, onApprove, onDiscard, onClose }: WhatsNewDraftModalProps) {
  const people = draft.recipient_count === 1 ? '1 person' : `${draft.recipient_count} people`;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2">
            <Mail size={18} />
            Friday email draft
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 pointer-coarse:p-2.5 pointer-coarse:-m-1 rounded-lg hover:bg-gray-100 transition" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">
            Nothing has been sent. This is exactly what goes out, to {people}
            {draft.sender_only ? ' (staging sends only to you)' : ' (facilitators and staff)'}.
            {' '}{draft.entry_count} update{draft.entry_count === 1 ? '' : 's'}.
          </p>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <p className="px-4 py-2 border-b border-gray-100 bg-off-white text-sm font-semibold text-navy">
              {draft.subject}
            </p>
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={draft.html_body}
              className="w-full h-[55vh] bg-white"
            />
          </div>
          {!draft.can_approve && (
            <p className="text-xs text-gray-500">A super admin approves the Friday email. You can discard it or leave it for them.</p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            {draft.can_approve && (
              <button
                type="button"
                disabled={busy || draft.recipient_count === 0}
                onClick={onApprove}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Approve and send to {people}
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={onDiscard}
              className="px-4 py-2.5 border border-red-200 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
            >
              Discard
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition disabled:opacity-50"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
